/**
 * Ink outlines via the inverted-hull technique.
 *
 * For each outlined mesh we add a sibling copy that renders BACK faces only,
 * expanded along its normals. The front faces of the real mesh cover the middle,
 * leaving a rim of outline color around the silhouette.
 *
 * WHY NOT post-process edge detection (see ARCHITECTURE.md):
 *   - No full-screen pass, so no extra bandwidth cost on mobile GPUs.
 *   - Works with MSAA, which post-process depth/normal edges famously do not.
 *   - Cost scales with outlined object count, which we control per quality tier,
 *     rather than with screen resolution, which we do not.
 * The tradeoff is that it cannot draw interior creases, only silhouettes. For a
 * chunky cartoon look that is exactly what we want anyway.
 */

import * as THREE from 'three';
import { tuning } from '@/core/tuning';

const vertexShader = /* glsl */ `
  #include <common>
  #include <skinning_pars_vertex>

  uniform float thickness;

  // Welded, averaged normals. See computeOutlineNormals() for why the regular
  // "normal" attribute cannot be used here.
  attribute vec3 outlineNormal;

  void main() {
    vec3 transformed = vec3(position);
    vec3 objectNormal = outlineNormal;
    #include <skinbase_vertex>
    #include <skinnormal_vertex>
    #include <skinning_vertex>

    // Expand along the view-space normal, scaled by view depth so the outline
    // stays a constant width on screen no matter how far away the object is.
    // Without the depth scale, distant props get hairline outlines and nearby
    // ones get comically fat ones.
    vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
    vec3 viewNormal = normalize(normalMatrix * objectNormal);
    float depth = max(0.001, -mvPosition.z);
    mvPosition.xyz += viewNormal * thickness * depth;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 outlineColor;

  void main() {
    gl_FragColor = vec4(outlineColor, 1.0);
  }
`;

/**
 * Which budget group an outline belongs to.
 *
 * The low quality tier keeps outlines on CHARACTERS only and drops them on the
 * world, which is where most of the cost is (a level has ~26 world outlines
 * against 4 character ones). Characters keep theirs because the cat and granny
 * are what the player is actually looking at.
 */
export type OutlineClass = 'character' | 'world';

export interface OutlineOptions {
  thickness?: number;
  color?: string;
  /** Defaults to 'world'. Pass 'character' for the cat, granny, dog, rival cat. */
  outlineClass?: OutlineClass;
}

/* ------------------------------------------------------------------ */
/* Smooth normals for the shell                                        */
/* ------------------------------------------------------------------ */

/**
 * Builds welded, averaged normals and stores them as an `outlineNormal` attribute.
 *
 * THIS IS LOAD-BEARING. Inverted-hull outlines expand each vertex along its
 * normal, which only produces a continuous shell if neighbouring faces share a
 * normal at every shared position. A BoxGeometry has HARD normals - its eight
 * corners are duplicated three times each, once per face, with three different
 * normals. Expanding along those moves the six faces apart independently, leaving
 * gaps at every edge, and the outline vanishes entirely.
 *
 * Smooth-normal shapes (spheres, capsules) happen to work by accident, which is
 * exactly why this bug is easy to miss: the cat had an outline and every piece of
 * furniture did not.
 *
 * We cannot simply smooth the mesh's real normals - that would round the toon
 * shading on cubes and make the furniture look like inflated cushions. So the
 * averaged normals live in a SECOND attribute used only by the outline shader,
 * which is the standard solution to this problem.
 */
export function computeOutlineNormals(
  geometry: THREE.BufferGeometry,
): THREE.Float32BufferAttribute {
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const count = position.count;
  const averaged = new Float32Array(count * 3);

  // Bucket vertices by quantized position so coincident corners are welded.
  // 1e4 is well below any geometry detail we use and well above float noise.
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < count; i++) {
    const key =
      `${Math.round(position.getX(i) * 1e4)},` +
      `${Math.round(position.getY(i) * 1e4)},` +
      `${Math.round(position.getZ(i) * 1e4)}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(i);
    else buckets.set(key, [i]);
  }

  for (const indices of buckets.values()) {
    let nx = 0;
    let ny = 0;
    let nz = 0;
    for (const i of indices) {
      nx += normal.getX(i);
      ny += normal.getY(i);
      nz += normal.getZ(i);
    }
    const length = Math.hypot(nx, ny, nz) || 1;
    nx /= length;
    ny /= length;
    nz /= length;
    for (const i of indices) {
      averaged[i * 3] = nx;
      averaged[i * 3 + 1] = ny;
      averaged[i * 3 + 2] = nz;
    }
  }

  return new THREE.Float32BufferAttribute(averaged, 3);
}

/** Adds `outlineNormal` to a geometry once. Safe to call repeatedly. */
function ensureOutlineNormals(geometry: THREE.BufferGeometry): void {
  if (geometry.getAttribute('outlineNormal')) return;
  if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
  geometry.setAttribute('outlineNormal', computeOutlineNormals(geometry));
}

const materialCache = new Map<string, THREE.ShaderMaterial>();

function getOutlineMaterial(thickness: number, color: string): THREE.ShaderMaterial {
  const key = `${thickness}|${color}`;
  const cached = materialCache.get(key);
  if (cached) return cached;

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      thickness: { value: thickness },
      outlineColor: { value: new THREE.Color(color) },
    },
    // BackSide is the whole trick: we only draw the faces pointing away from the
    // camera, so the real mesh covers everything except the rim.
    side: THREE.BackSide,
    depthWrite: true,
  });

  materialCache.set(key, material);
  return material;
}

/**
 * Adds an outline shell to `mesh`. The shell is parented to the mesh, so it
 * inherits every transform automatically - including animation, if the mesh is
 * skinned and the caller uses `outlineSkinnedMesh`.
 *
 * Returns the shell so callers can toggle `.visible` per quality tier.
 */
export function addOutline(mesh: THREE.Mesh, options: OutlineOptions = {}): THREE.Mesh {
  const outlineClass = options.outlineClass ?? 'world';
  // Characters get a heavier line than the world so they read against it.
  const thickness =
    options.thickness ??
    (outlineClass === 'character'
      ? tuning.render.outlineThicknessCharacter
      : tuning.render.outlineThickness);
  const color = options.color ?? tuning.render.outlineColor;

  // Without this, every box in the game silently renders with NO outline.
  ensureOutlineNormals(mesh.geometry);

  const material = getOutlineMaterial(thickness, color);
  const shell =
    mesh instanceof THREE.SkinnedMesh
      ? createSkinnedOutline(mesh, material)
      : new THREE.Mesh(mesh.geometry, material);
  shell.name = `${mesh.name || 'mesh'}:outline`;
  shell.userData.outlineClass = outlineClass;
  // The shell must never cast or receive shadows - it would self-shadow the
  // object it is outlining and produce a dark halo.
  shell.castShadow = false;
  shell.receiveShadow = false;
  shell.renderOrder = (mesh.renderOrder || 0) - 1;
  mesh.add(shell);
  return shell;
}

function createSkinnedOutline(
  source: THREE.SkinnedMesh,
  material: THREE.ShaderMaterial,
): THREE.SkinnedMesh {
  const shell = new THREE.SkinnedMesh(source.geometry, material);
  shell.bindMode = source.bindMode;
  shell.bind(source.skeleton, source.bindMatrix);
  shell.bindMatrixInverse.copy(source.bindMatrixInverse);
  if (source.morphTargetInfluences) {
    shell.morphTargetInfluences = [...source.morphTargetInfluences];
    shell.morphTargetDictionary = { ...source.morphTargetDictionary };
  }
  return shell;
}

/**
 * Outlines every mesh in a subtree. Used on characters and hero props.
 * Skips anything already carrying an outline, so it is safe to call twice.
 */
export function addOutlinesRecursive(
  root: THREE.Object3D,
  options: OutlineOptions = {},
): THREE.Mesh[] {
  const shells: THREE.Mesh[] = [];
  const meshes: THREE.Mesh[] = [];

  root.traverse((node) => {
    if (node instanceof THREE.Mesh && !node.name.endsWith(':outline')) meshes.push(node);
  });

  for (const mesh of meshes) {
    const alreadyOutlined = mesh.children.some((child) => child.name.endsWith(':outline'));
    if (alreadyOutlined) continue;
    shells.push(addOutline(mesh, options));
  }
  return shells;
}

/** Shows/hides every outline shell in a subtree. */
export function setOutlinesVisible(root: THREE.Object3D, visible: boolean): void {
  root.traverse((node) => {
    if (node.name.endsWith(':outline')) node.visible = visible;
  });
}

/**
 * Applies a quality tier's outline setting.
 *
 * 'all'             -> every outline visible
 * 'charactersOnly'  -> world outlines hidden, characters keep theirs
 *
 * This is what makes the low tier genuinely cheaper: for level 1 it drops the
 * draw call count from ~73 to ~47.
 */
export function applyOutlineQuality(
  root: THREE.Object3D,
  setting: 'all' | 'charactersOnly',
): { shown: number; hidden: number } {
  let shown = 0;
  let hidden = 0;

  root.traverse((node) => {
    if (!node.name.endsWith(':outline')) return;
    const isCharacter = node.userData.outlineClass === 'character';
    const visible = setting === 'all' || isCharacter;
    node.visible = visible;
    if (visible) shown++;
    else hidden++;
  });

  return { shown, hidden };
}

/** Count of outline shells in a subtree, split by class. Used by the debug HUD. */
export function countOutlines(root: THREE.Object3D): { character: number; world: number } {
  let character = 0;
  let world = 0;
  root.traverse((node) => {
    if (!node.name.endsWith(':outline')) return;
    if (node.userData.outlineClass === 'character') character++;
    else world++;
  });
  return { character, world };
}

export function disposeOutlineMaterials(): void {
  for (const material of materialCache.values()) material.dispose();
  materialCache.clear();
}
